/**
 * QuickFiltersBar — Faza B v2 (Status General)
 *
 * Bar compact cu 9 preset-uri predefinite pentru operatorii BO/PPC.
 * - Fetch live counters din /api/dashboard/quick-filter-counts (RBAC-aware).
 * - Click → aplică `preset=<key>` pe /api/sales (fără a distruge alte filtre custom).
 * - Preset-urile cu 0 rezultate sunt afișate disabled.
 * - Toggle: al doilea click deselectează preset-ul activ.
 *
 * Ordine confirmată de user:
 *  1. Probleme
 *  2. Rework activ
 *  3. Conflicte blocante
 *  4. Anulate cu Rework
 *  5. Nou fără procesare
 *  6. Active cu avertismente
 *  7. Status neactualizat
 *  8. În verificare BO
 *  9. În procesare PPC
 *
 * RBAC vizual: bara e vizibilă pentru toate rolurile; contoarele sunt deja
 * scoped RBAC pe backend (agent vede doar own, agenție → own agency, etc.).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertOctagon, RotateCcw, Ban, HelpCircle, AlertTriangle,
  Clock, RefreshCw, Send, Loader2,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Ordinea cerută de user (nu schimba fără cerere explicită).
const PRESET_DEFS = [
  { key: "problems",              label: "Probleme",                  Icon: AlertOctagon, color: "rose" },
  { key: "rework_active",         label: "Rework activ",              Icon: RotateCcw,    color: "amber" },
  { key: "blocking_conflicts",    label: "Conflicte blocante",        Icon: AlertOctagon, color: "red" },
  { key: "cancelled_with_rework", label: "Anulate cu Rework",         Icon: Ban,          color: "orange" },
  { key: "new_without_processing",label: "Nou fără procesare",        Icon: HelpCircle,   color: "slate" },
  { key: "active_with_warnings",  label: "Active cu avertismente",    Icon: AlertTriangle,color: "emerald" },
  { key: "stale_status",          label: "Status neactualizat",       Icon: Clock,        color: "yellow" },
  { key: "processing_bo",         label: "În verificare BO",          Icon: RefreshCw,    color: "purple" },
  { key: "processing_ppc",        label: "În procesare PPC",          Icon: Send,         color: "blue" },
];

const COLOR_CLASSES = {
  rose:    { on: "bg-rose-600 text-white border-rose-600 hover:bg-rose-700",       off: "bg-white text-rose-700 border-rose-200 hover:bg-rose-50" },
  amber:   { on: "bg-amber-500 text-white border-amber-500 hover:bg-amber-600",    off: "bg-white text-amber-800 border-amber-200 hover:bg-amber-50" },
  red:     { on: "bg-red-700 text-white border-red-700 hover:bg-red-800",          off: "bg-white text-red-800 border-red-200 hover:bg-red-50" },
  orange:  { on: "bg-orange-600 text-white border-orange-600 hover:bg-orange-700", off: "bg-white text-orange-800 border-orange-200 hover:bg-orange-50" },
  slate:   { on: "bg-slate-700 text-white border-slate-700 hover:bg-slate-800",    off: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" },
  emerald: { on: "bg-emerald-700 text-white border-emerald-700 hover:bg-emerald-800", off: "bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50" },
  yellow:  { on: "bg-yellow-500 text-black border-yellow-500 hover:bg-yellow-600", off: "bg-white text-yellow-800 border-yellow-200 hover:bg-yellow-50" },
  purple:  { on: "bg-purple-700 text-white border-purple-700 hover:bg-purple-800", off: "bg-white text-purple-800 border-purple-200 hover:bg-purple-50" },
  blue:    { on: "bg-blue-700 text-white border-blue-700 hover:bg-blue-800",       off: "bg-white text-blue-800 border-blue-200 hover:bg-blue-50" },
};

export const QuickFiltersBar = ({ activePreset, onSelect }) => {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCounts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/dashboard/quick-filter-counts`);
      setCounts(r.data?.counts || {});
    } catch {
      setCounts({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (key) => {
    if (activePreset === key) {
      onSelect(null); // toggle off
    } else {
      onSelect(key);
    }
  };

  return (
    <Card className="p-3" data-testid="quick-filters-bar">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold uppercase text-slate-500">
          Filtre rapide · Status general
        </span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        {activePreset && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-[11px] text-slate-500 hover:text-rose-600 underline ml-auto"
            data-testid="quick-filters-clear"
          >
            Resetează preset
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {PRESET_DEFS.map(({ key, label, Icon, color }) => {
          const count = counts?.[key];
          const isActive = activePreset === key;
          const isDisabled = count === 0 && !isActive;
          const cls = COLOR_CLASSES[color] || COLOR_CLASSES.slate;
          const finalCls = isActive ? cls.on : cls.off;
          return (
            <button
              key={key}
              type="button"
              onClick={() => !isDisabled && handleClick(key)}
              disabled={isDisabled}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${finalCls} ${
                isDisabled ? "opacity-50 cursor-not-allowed" : ""
              }`}
              data-testid={`quick-filter-${key}`}
              title={isDisabled ? `Niciun rezultat pentru "${label}"` : `Aplică filtru: ${label}`}
              aria-pressed={isActive}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{label}</span>
              {count !== undefined ? (
                <Badge
                  variant="secondary"
                  className={`ml-1 h-4 px-1.5 text-[10px] ${
                    isActive ? "bg-white/20 text-white border-white/40" : "bg-slate-100 text-slate-700"
                  }`}
                  data-testid={`quick-filter-count-${key}`}
                >
                  {count}
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className={`ml-1 h-4 px-1.5 text-[10px] opacity-50 ${
                    isActive ? "bg-white/20 text-white border-white/40" : "bg-slate-100 text-slate-500"
                  }`}
                  data-testid={`quick-filter-count-${key}`}
                >
                  …
                </Badge>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
};

export default QuickFiltersBar;
